import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useTransactionSetCompletedMutation } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { Button, CalendarInput, CustomCheckbox, ModalContentWrapper, TextInputField } from 'ui';

dayjs.extend(utc);

const validationSchema = yup.object({
  do_not_notify_investor: yup.boolean().required('This select is required'),
});

type FormValues = {
  id: number;
  class_name: string;
  quantity: number;
  price_per_share: number;
  total: number;
  issue_date: string;
  memo: string;
  do_not_notify_investor: boolean;
};

export interface IssuerConfirmTransactionCompletedModalProps {
  id: number;
  open: boolean;
  onClose: () => void;
  register_class_name: string;
  quantity: number;
  price_per_share: number;
  total: number;
  issue_date?: string;
  memo?: string;
}

export const IssuerConfirmTransactionCompletedModal = ({
  id,
  open,
  onClose,
  register_class_name,
  quantity,
  price_per_share,
  total,
  issue_date,
  memo,
}: IssuerConfirmTransactionCompletedModalProps) => {
  const { mutateAsync: completeTransaction, isLoading } = useTransactionSetCompletedMutation({
    onSuccess: onClose,
  });
  const formik = useFormik<FormValues>({
    initialValues: {
      id,
      class_name: register_class_name || '',
      quantity,
      price_per_share,
      total,
      issue_date: issue_date || '',
      memo: memo || '',
      do_not_notify_investor: false,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async values => {
      await completeTransaction({
        id: values.id,
        do_not_notify_investor: values.do_not_notify_investor,
      });
    },
    enableReinitialize: true,
  });

  return (
    <ModalContentWrapper open={open} title="Confirm transaction" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <TextInputField
              name="class_name"
              label="Class"
              value={register_class_name || '-'}
              disabled={true}
            />
            <CurrencyInput
              name="quantity"
              label="Units/shares"
              value={quantity}
              onChange={() => 0}
              disabled={true}
            />
            <CurrencyInput
              name="price_per_share"
              label="Price"
              value={price_per_share}
              onChange={() => 0}
              disabled={true}
            />
            <CurrencyInput
              name="total"
              label="Total"
              value={total}
              onChange={() => 0}
              disabled={true}
            />
            {!!issue_date && (
              <CalendarInput
                label="Issue date"
                name="issue_date"
                value={issue_date ? dayjs(issue_date).utc() : undefined}
                onChange={() => 0}
                showTime={true}
                format="DD-MM-YYYY HH:mm"
                disabled={true}
              />
            )}
            <TextInputField
              textarea
              name="memo"
              placeholder="Memo..."
              label="Memo (optional)"
              className="resize-none"
              value={memo || '-'}
              disabled={true}
            />
            <CustomCheckbox
              label="Do not notify investor"
              checked={formik.values.do_not_notify_investor}
              onChange={e => formik.setFieldValue('do_not_notify_investor', e.target.checked)}
            />
            <Button onClick={formik.submitForm} disabled={isLoading} loading={isLoading}>
              Confirm
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
